import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  fetchEC2Instances,
  fetchRDSInstances,
  startEC2Instance,
  stopEC2Instance,
  startRDSInstance,
  stopRDSInstance,
} from '@/api/awsResources';

// Shared retry / refetch logic — same pattern as useNamespaces.
// Don't poll when AWS creds aren't configured (503) or access is denied.
const shouldRetry = (failureCount: number, err: unknown) => {
  if (err instanceof AxiosError) {
    const s = err.response?.status;
    if (s === 503 || s === 403 || s === 401) return false;
  }
  return failureCount < 2;
};

const refetchInterval = (query: { state: { error: unknown } }) => {
  const err = query.state.error;
  if (err instanceof AxiosError) {
    const s = err.response?.status;
    if (s === 503 || s === 403 || s === 401) return false;
  }
  return 30 * 1000;
};

// ------------------------------------------------------------------
// Burst polling
//
// AWS state transitions (running → stopping → stopped, and the reverse)
// take a few seconds each. Our normal 30s poll means the UI can sit on
// a stale state for up to 30 seconds after the user clicks Start/Stop,
// which feels broken ("did my click work?"). To fix the perceived lag
// without paying the bandwidth cost of always-fast polling, we burst
// every BURST_INTERVAL_MS for BURST_DURATION_MS right after a mutation
// succeeds, then drop back to normal polling.
// ------------------------------------------------------------------

const BURST_INTERVAL_MS = 3_000;
const BURST_DURATION_MS = 60_000;

/**
 * Invalidate `queryKey` repeatedly for ~60 seconds. Each invalidation
 * triggers React Query to refetch, so the UI catches state changes
 * within a few seconds instead of waiting for the next 30s poll tick.
 *
 * Multiple bursts on the same key just stack timer cancellations — the
 * last one wins, which is what we want.
 */
function startBurstPoll(queryClient: ReturnType<typeof useQueryClient>, queryKey: QueryKey) {
  const deadline = Date.now() + BURST_DURATION_MS;
  const tick = () => {
    if (Date.now() > deadline) return;
    queryClient.invalidateQueries({ queryKey });
    window.setTimeout(tick, BURST_INTERVAL_MS);
  };
  // Kick the first tick on the next event-loop turn so the mutation's
  // own onSuccess invalidation lands first.
  window.setTimeout(tick, BURST_INTERVAL_MS);
}

// ------------------------------------------------------------------
// EC2
// ------------------------------------------------------------------

const EC2_KEY: QueryKey = ['aws', 'ec2'];

export function useEC2Instances() {
  return useQuery({
    queryKey: EC2_KEY,
    queryFn: fetchEC2Instances,
    staleTime: 15 * 1000,
    refetchInterval,
    retry: shouldRetry,
  });
}

export function useStartEC2Instance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (instanceId: string) => startEC2Instance(instanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EC2_KEY });
      startBurstPoll(queryClient, EC2_KEY);
    },
  });
}

export function useStopEC2Instance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (instanceId: string) => stopEC2Instance(instanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EC2_KEY });
      startBurstPoll(queryClient, EC2_KEY);
    },
  });
}

// ------------------------------------------------------------------
// RDS
// ------------------------------------------------------------------

const RDS_KEY: QueryKey = ['aws', 'rds'];

export function useRDSInstances() {
  return useQuery({
    queryKey: RDS_KEY,
    queryFn: fetchRDSInstances,
    staleTime: 15 * 1000,
    refetchInterval,
    retry: shouldRetry,
  });
}

export function useStartRDSInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dbIdentifier: string) => startRDSInstance(dbIdentifier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RDS_KEY });
      startBurstPoll(queryClient, RDS_KEY);
    },
  });
}

export function useStopRDSInstance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dbIdentifier: string) => stopRDSInstance(dbIdentifier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RDS_KEY });
      startBurstPoll(queryClient, RDS_KEY);
    },
  });
}
